import clsx from 'clsx'
import { useEffect } from 'react'

import { lockBodyScroll } from '@/shared/lib/dom/lock-body-scroll'
import { Button } from '@/shared/ui/Button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Drawer } from '@/shared/ui/drawer'
import { Icon } from '@/shared/ui/icon'
import { Radio } from '@/shared/ui/radio'

import type { Option, SelectCloseControl } from '../../types/types'

import cl from './mobile-select.module.scss'

export type MobileSelectProps = {
	className?: string
	title: string
	options: Option[]
	value?: string | string[]
	isOpen: boolean
	onClose: () => void
	onChange: (value: string | string[]) => void
	resetLabel?: string
	selectionStyle?: 'highlight' | 'radio'
	closeControl?: SelectCloseControl
}

export type MobileSelectTriggerProps = {
	className?: string
	variant?: 'default' | 'outlined'
	label?: string
	onClick: () => void
}

export const MobileSelectTrigger = (props: MobileSelectTriggerProps) => {
	const { className, variant = 'default', label, onClick } = props

	return (
		<Button
			type="button"
			className={clsx(cl.trigger, variant === 'outlined' && cl._outlined, className)}
			theme="clear"
			onClick={onClick}>
			<span className={cl.trigger__label}>{label}</span>
			<Icon name="ArrowDown" size={18} />
		</Button>
	)
}

export const MobileSelect = (props: MobileSelectProps) => {
	const {
		className,
		title,
		options,
		value,
		isOpen,
		onClose,
		onChange,
		resetLabel,
		selectionStyle = 'highlight',
		closeControl = 'handle'
	} = props
	const isMultiple = Array.isArray(value)

	useEffect(() => {
		if (isOpen) return lockBodyScroll()
	}, [isOpen])

	const selectOption = (option: Option) => {
		const optionValue = option.value ?? option.label

		if (isMultiple) {
			onChange(value.includes(optionValue) ? value.filter(current => current !== optionValue) : [...value, optionValue])
			return
		}

		if (value === optionValue) return

		onChange(optionValue)
		onClose()
	}

	return (
		<Drawer
			animateBackdrop
			className={clsx(cl.drawer, className)}
			contentClassName={cl.drawerContent}
			isOpen={isOpen}
			onClose={onClose}
			direction="bottom"
			topPanelClassName={cl.drawerTopPanel}
			topPanel={
				closeControl !== 'none' && (
					<button
						type="button"
						className={clsx(cl.closeControl, closeControl === 'handle' ? cl._handle : cl._cross)}
						aria-label="Закрыть"
						onClick={onClose}>
						{closeControl === 'cross' && <Icon name="Close" size={24} />}
					</button>
				)
			}>
			<div className={cl.content}>
				<p className={clsx(cl.title, closeControl === 'cross' && cl._withCross)}>{title}</p>
				<div className={cl.list}>
					{resetLabel && isMultiple && (
						<button
							type="button"
							className={cl.option}
							onClick={() => {
								if (value.length > 0) onChange([])
							}}>
							<span>{resetLabel}</span>
							<Checkbox className={cl.checkbox} checked={value.length === 0} onChange={() => undefined} tabIndex={-1} />
						</button>
					)}
					{options.map(option => {
						const optionValue = option.value ?? option.label
						const isSelected = isMultiple ? value.includes(optionValue) : value === optionValue

						return (
							<button
								type="button"
								key={optionValue}
								className={clsx(
									cl.option,
									isSelected && cl.option_selected,
									!isMultiple && selectionStyle === 'highlight' && cl._highlight
								)}
								onClick={() => selectOption(option)}>
								<span>{option.content ?? option.label}</span>
								{isMultiple ? (
									<Checkbox className={cl.checkbox} checked={isSelected} onChange={() => undefined} tabIndex={-1} />
								) : selectionStyle === 'radio' ? (
									<Radio className={cl.radio} checked={isSelected} onChange={() => undefined} tabIndex={-1} />
								) : null}
							</button>
						)
					})}
				</div>
			</div>
		</Drawer>
	)
}
